import { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ItemPhoto } from "@egalregal/shared";
import { useT } from "../../shared/i18n";
import { requests } from "../../shared/api";
import { ItemPhotoTile, itemPhotoActions } from "./ItemPhotoTile";
import { CaptureToolbar, type StagedCapture } from "../capture/CaptureToolbar";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * The item photo gallery section: the shared camera + file capture toolbar
 * (photo-only, mirrors the container UI minus audio memos) plus the tile grid,
 * with the empty state underneath. Captures streamed up via onStagedCapture.
 */
export function PhotosSection({
  itemId,
  itemName,
  identity,
  photos,
  onStagedCapture,
  onOpenPhoto,
  onRemovePhoto,
  onReorder,
}: {
  itemId: string;
  itemName: string;
  identity?: { uuid: string; name: string };
  photos: ItemPhoto[];
  onStagedCapture: (capture: StagedCapture) => void;
  onOpenPhoto: (index: number) => void;
  onRemovePhoto: (photoId: string) => void;
  onReorder: (ids: string[]) => void;
}) {
  const t = useT();
  const readOnly = useDemoMode();
  const orderedIds = photos.map((photo) => photo.id);
  // Item photos persist no filename, but keep same-session captures unique so
  // the camera dialog's collision suffixes still hold while the item is open.
  const [stagedNames, setStagedNames] = useState<string[]>([]);

  const titleOf = (photo: ItemPhoto) =>
    photo.type === "nutrition" ? t("itemNutrition") : t("itemPhotos");

  /**
   * Full panel data for every other photo: the panel's manage actions must act
   * on the sibling the user selected, not on the tile's original photo.
   */
  const relatedOf = (photo: ItemPhoto) =>
    photos
      .filter((sibling) => sibling.id !== photo.id)
      .slice(0, 32)
      .map((sibling) => {
        const index = photos.findIndex((entry) => entry.id === sibling.id);
        return {
          key: sibling.id,
          image: requests.media.itemPhotoPreviewUrl(itemId, sibling.id),
          alt: itemName,
          // Item photos have no stored name: the panel always names the item,
          // while the selected sibling's kind is its subtitle.
          title: itemName,
          secondary: titleOf(sibling),
          identity,
          actions: readOnly
            ? []
            : itemPhotoActions(sibling, index, {
                t,
                count: photos.length,
                orderedIds,
                onRemove: onRemovePhoto,
                onReorder,
              }),
          onOpen: () => onOpenPhoto(index),
        };
      });

  const stageCapture = (capture: StagedCapture) => {
    setStagedNames((prev) => [...prev, capture.file.name]);
    onStagedCapture(capture);
  };

  return (
    <Box sx={{ mb: 3 }} data-testid="item-photos-section">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {t("itemPhotos")}
        </Typography>
      </Stack>
      {!readOnly && (
        <CaptureToolbar onStaged={stageCapture} photoOnly usedNames={stagedNames} />
      )}
      {photos.length > 0 ? (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {photos.map((photo, idx) => (
            <Grid item key={photo.id} xs={6} sm={4} md={3}>
              <ItemPhotoTile
                itemId={itemId}
                itemName={itemName}
                identity={identity}
                related={relatedOf(photo)}
                photo={photo}
                index={idx}
                count={photos.length}
                orderedIds={orderedIds}
                onOpen={onOpenPhoto}
                onRemove={onRemovePhoto}
                onReorder={onReorder}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("itemNoPhotos")}
        </Typography>
      )}
    </Box>
  );
}